import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MultiSelect,
  multiSelectVariant
} from "@/components/controls/MultiSelect";
import { useEntityMultiSelect } from "@/services/hooks/entities/useEntityMultiSelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEntityTypeFromDisplayType } from "@/utils/entityFieldHelpers";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { AddButtonFooter } from "@/components/ui/add-button-footer";

interface EntityMultiSelectFieldProps {
  fieldName: string;
  displayType: string;
  value?: string[] | null;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  dependentValues?: Record<string, string | null>;
  onCreateNew?: () => void;
  createNewLabel?: string;
  config?: { placeholder?: string };
  variant?: multiSelectVariant;
  className?: string;
  startEmpty?: boolean;
}

const EntityMultiSelectField: React.FC<EntityMultiSelectFieldProps> = ({
  displayType,
  value,
  onChange,
  placeholder,
  disabled = false,
  dependentValues,
  onCreateNew,
  createNewLabel,
  config,
  variant = "form",
  className,
  startEmpty = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const entityType = getEntityTypeFromDisplayType(displayType);

  const selectedIds = useMemo(() => (value ?? []).filter(Boolean), [value]);

  const partnerId = dependentValues?.partner || null;
  const filters = useMemo(
    () =>
      displayType === FieldDisplayType.PARTNER_CONTACTS && partnerId
        ? { partner: partnerId }
        : {},
    [displayType, partnerId]
  );

  const { options, isLoading, onSearch, setSearchTerm } = useEntityMultiSelect({
    entityType: entityType ?? EntityType.PARTNER_CONTACT,
    selectedIds,
    filters
  });

  const visibleOptions = useMemo(
    () =>
      startEmpty
        ? options.filter((option) => selectedIds.includes(option.value))
        : options,
    [startEmpty, options, selectedIds]
  );

  if (!entityType) {
    return null;
  }

  const popOverFooter =
    onCreateNew && createNewLabel ? (
      <AddButtonFooter
        buttonText={createNewLabel}
        onClick={() => {
          setIsOpen(false);
          onCreateNew();
        }}
      />
    ) : undefined;

  return (
    <MultiSelect
      options={visibleOptions}
      selectedValues={selectedIds}
      onValueChange={(newValues) => onChange?.(newValues)}
      placeholder={placeholder || config?.placeholder || t("select")}
      disabled={disabled}
      entity={entityType}
      showSearch
      onSearch={onSearch}
      isLoading={isLoading}
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setSearchTerm("");
        }
      }}
      popOverFooter={popOverFooter}
      matchTriggerWidth
      showIconInChip
      maxCount={5}
      variant={variant}
      className={className}
      placeholderClassName="text-tertiary"
      activeClassName="!border-primary"
    />
  );
};

export default EntityMultiSelectField;
